import type { BlockComponentProps } from 'client-react-block-renderer';
import React, { useEffect, useState } from 'react';
import t from 'rcm-i18n';
import getAppState from 'app-state';
import { RadioField, InputTextField } from 'forms';
import { getOutputUrl, getInputUrl } from './UrlCreators';
import './DistributorLinkCreatorBlockComponent.scss';

type Props = BlockComponentProps<Data, Config>;

interface Config {
  accountTypeOptions: string;
}

interface Data {}

const DistributorLinkCreatorBlockComponent = (props: Props) => {
  const [hostname, setHostName] = useState(location.hostname);
  const [currentUserAppState, setCurrentUserAppState] = useState(null);
  const [linkType, setLinkType] = useState('joinReliv');
  const [selectedRank, setSelectedRank] = useState('1');
  const [customInputUrl, setCustomInputUrl] = useState(location.protocol + '//' + hostname + '/join-reliv');
  const [couponCode, setCouponCode] = useState('');

  useEffect(() => {
    setCurrentUserAppState(getAppState('currentUser'));
  }, []);

  const handleSelectedRankChange = (newValue: string) => {
    setSelectedRank(newValue);
  };

  const handleLinkTypeChange = (newValue: string) => {
    setLinkType(newValue);
  };

  const handleCouponCodeChange = (newValue: string) => {
    setCouponCode(newValue);
  };

  const handleCustomInputUrlChange = (newValue: string) => {
    setCustomInputUrl(newValue);
  };

  if (currentUserAppState === null) {
    return <div></div>; // Show blank page while loading.
  }
  // FIXME: We don't need the `userFound` flag. This can be done using `currentUserAppState.profile != null` or similar.
  if (!currentUserAppState.userFound || !['D', 'E'].includes(currentUserAppState.profile.status)) {
    return (
      <>
        <div className="alert alert-warning">{t('You must be logged in as a distributor to use this tool.')}</div>
        <a className="btn btn-primary" href={'/login?redirect=' + encodeURIComponent(window.location.pathname)}>
          {t('login')}
        </a>
      </>
    );
  }

  const outputUrl = getOutputUrl(
    getInputUrl(linkType, hostname, selectedRank, customInputUrl),
    currentUserAppState.userId,
    couponCode
  );

  return (
    <div>
      <div className="rcmOnlineApplication RcmForm ng-scope DistributorLinkCreatorBlockComponent">
        <div data-ng-hide="submissionComplete">
          <div className="ng-isolate-scope">
            <h1>{t('Link Creator')}</h1>
            <p></p>{' '}
          </div>
          <form>
            <p>
              {t(
                'This tool allows you to create sign up and affiliate links. These links contain your RCN and link to Reliv web pages. If someone visits one of these links and signs up, your RCN will be automatically filled in as the sponsor RCN on the signup form.'
              )}
            </p>
            <div>
              {outputUrl.length !== 0 && (
                <div className="alert alert-info sign-someone-up-link-wrap">
                  <p>{t('Here is your link:')}</p>
                  <br />
                  <strong>
                    <a target="_blank" href={outputUrl}>
                      {outputUrl}
                    </a>
                  </strong>
                  <br />
                  <br />
                  <p>{t('Copy and paste it to share it via email, text message, websites, or social media.')}</p>
                </div>
              )}
              <div className="panel panel-default">
                <div className="panel-heading">{t('Link Options')}</div>
                <div className="panel-body">
                  <div className="form-group">
                    <label>{t('Link Type')}</label>
                    <RadioField
                      options={[
                        { value: 'joinReliv', label: t('Link to the Join Reliv page') },
                        { value: 'customerForm', label: t('Link to the customer signup form') },
                        { value: 'preferredCustomerForm', label: t('Link to the preferred customer signup form') },
                        { value: 'distributorForm', label: t('Link to the distributor signup form') },
                        { value: 'custom', label: t('Link to any other Reliv page') },
                      ]}
                      value={linkType}
                      onValueChange={handleLinkTypeChange}
                    />
                  </div>
                  {linkType === 'custom' && (
                    <>
                      <div className="form-group">
                        <label htmlFor="customInputUrl">
                          {t(
                            'Reliv Page URL (This must be a URL on a Reliv website or a Reliv hosted personal website.)'
                          )}
                        </label>
                        <InputTextField
                          value={customInputUrl}
                          onValueChange={handleCustomInputUrlChange}
                          id="customInputUrl"
                        />
                      </div>
                    </>
                  )}
                  {linkType === 'distributorForm' && (
                    <>
                      <label>{t('Starting profit level')}</label>
                      <RadioField
                        options={[
                          // In the future these could be data driven per market
                          { value: '1', label: '20' + '% ' },
                          { value: '2', label: '25' + '% ' },
                          { value: '3', label: '30' + '% ' },
                          { value: '4', label: '35' + '% ' },
                        ]}
                        value={selectedRank}
                        onValueChange={handleSelectedRankChange}
                      />
                    </>
                  )}
                  <div className="form-group">
                    <label>
                      {t(
                        'Coupon Code (This embeds a coupon code into the link. Leave this blank if you do not have one.)'
                      )}
                    </label>
                    <InputTextField value={couponCode} onValueChange={handleCouponCodeChange} />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
export default DistributorLinkCreatorBlockComponent;
